import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import type {
  CreateStartListGroupRequest,
  StartListGroup,
  StartListTarget,
} from '../../types/startList';
import { START_LIST_TARGET_LABELS } from '../../types/startList';
import {
  AGE_SUBCATEGORY_LABELS,
  AGE_SUBCATEGORY_ORDER,
  type AgeSubcategory,
} from '../../types/individualEntry';

interface Props {
  open: boolean;
  group: StartListGroup | null;
  loading: boolean;
  /** Pokud disciplina je individualni — defaultne nabidneme INDIVIDUAL. */
  defaultTarget?: StartListTarget;
  onCancel: () => void;
  onSubmit: (req: CreateStartListGroupRequest) => void;
}

export function StartListGroupEditor({
  open,
  group,
  loading,
  defaultTarget = 'INDIVIDUAL',
  onCancel,
  onSubmit,
}: Props) {
  const [name, setName] = useState('');
  const [target, setTarget] = useState<StartListTarget>(defaultTarget);
  const [ageSubcategories, setAgeSubcategories] = useState<AgeSubcategory[]>([]);
  const [parallelLanes, setParallelLanes] = useState<string>('1');

  useEffect(() => {
    if (open) {
      if (group) {
        setName(group.name);
        setTarget(group.target);
        setAgeSubcategories(group.ageSubcategories ?? []);
        setParallelLanes(String(group.parallelLanes ?? 1));
      } else {
        setName('');
        setTarget(defaultTarget);
        setAgeSubcategories([]);
        setParallelLanes('1');
      }
    }
  }, [open, group, defaultTarget]);

  const submit = () => {
    const lanes = Math.max(1, Number(parallelLanes) || 1);
    onSubmit({
      name: name.trim(),
      target,
      ageSubcategories: target === 'TEAM' ? null : ageSubcategories.length > 0 ? ageSubcategories : null,
      parallelLanes: lanes,
    });
  };

  const canSubmit = name.trim().length > 0 && Number(parallelLanes) >= 1;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? 'Upravit startovací skupinu' : 'Nová startovací skupina'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Název skupiny"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            placeholder="např. Mladší + střední dorostenci"
          />
          <FormControl fullWidth>
            <InputLabel>Typ</InputLabel>
            <Select
              label="Typ"
              value={target}
              onChange={(e) => {
                const next = e.target.value as StartListTarget;
                setTarget(next);
                if (next === 'TEAM') setAgeSubcategories([]);
              }}
            >
              <MenuItem value="INDIVIDUAL">{START_LIST_TARGET_LABELS.INDIVIDUAL}</MenuItem>
              <MenuItem value="TEAM">{START_LIST_TARGET_LABELS.TEAM}</MenuItem>
            </Select>
          </FormControl>
          {target === 'INDIVIDUAL' && (
            <FormControl fullWidth>
              <InputLabel>Věkové podkategorie</InputLabel>
              <Select<AgeSubcategory[]>
                multiple
                label="Věkové podkategorie"
                value={ageSubcategories}
                onChange={(e) => {
                  const value = e.target.value;
                  setAgeSubcategories(typeof value === 'string' ? (value.split(',') as AgeSubcategory[]) : value);
                }}
                renderValue={(selected) =>
                  selected.map((s) => AGE_SUBCATEGORY_LABELS[s]).join(', ')
                }
              >
                {AGE_SUBCATEGORY_ORDER.map((c) => (
                  <MenuItem key={c} value={c}>
                    <Checkbox checked={ageSubcategories.indexOf(c) > -1} />
                    <ListItemText primary={AGE_SUBCATEGORY_LABELS[c]} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box>
            <TextField
              label="Paralelní dráhy (počet)"
              type="number"
              inputProps={{ min: 1, max: 10 }}
              value={parallelLanes}
              onChange={(e) => setParallelLanes(e.target.value)}
              fullWidth
              helperText="Kolik závodníků/družstev běží současně v jednom rozběhu"
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Zrušit
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit || loading}>
          {group ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
